<template>
    <div class="flex flex-wrap items-center print:hidden w-full" ref="navBoundary">
      <section class="relative mx-auto w-full">
        <nav
          class="flex justify-between px-3.5 items-center bg-dark-primary text-white w-full h-14 z-50">
          <div class="flex items-center -mt-1" :class="{ 'w-96': windowWidth > navBreakpoint}">
            <NavLink
              :link="{ path: '/', label: 'Home'}"
              @track-link-selection="logLinkSelection"
            >
              <img class="h-8 object-fit" src="../assets/images/jet_upward.png" alt="logo">
            </NavLink>
            <BreadCrumb v-if="windowWidth > navBreakpoint" :title="currentTitle" class="-mt-0.5 truncate w-72" />
            <MenuBar v-if="hasInternalUserPermissions && showMenuBar" :userPermissions="userPermissions" />
          </div>
          <div v-if="windowWidth < navBreakpoint" class="flex justify-between">
            <div class="">
              <div class="hover:bg-[#4b5f7d] p-1 rounded-md cursor-pointer">
                <svg @click="toggleNavDropdown" class="fill-white" viewBox="0 0 100 80" width="25" height="25">
                  <rect width="100" height="10" rx="5" ry="5"></rect>
                  <rect y="30" width="100" height="10" rx="5" ry="5"></rect>
                  <rect y="60" width="100" height="10" rx="5" ry="5"></rect>
                </svg>
              </div>
            </div>
            <base-card v-if="isNavDropdownVisible" class="fixed top-12 left-0 w-full bg-white text-dark-primary rounded-t-none">
                <PrimePanelMenu hasInternalUserPermissions @navigate-to-page="navigateToPage" :links="verticalLinks" :user-permissions="userPermissions" />
                <PrimePanelMenu @navigate-to-page="navigateToPage" :links="Object.values(navbar_links)" :user-permissions="userPermissions" />
                  <NavLink 
                    class="ml-2 p-1 rounded-lg hover:bg-gray-100"
                    :link="{path: '/auth', label: 'Logout', displayLabel: true}"
                    @click="logout"
                    @track-link-selection="logLinkSelection"
                  />
                <!-- </li> -->
            </base-card>
          </div>
          <div v-else class="flex justify-between w-full items-center">
            <div v-if="hasInternalUserPermissions" class="flex flex-row justify-start mx-auto font-semibold font-heading items-center space-x-4 px-4 text-xs">
              <PrimeMenu
                v-for="[key, section] in Object.entries(navbar_links).filter(([k, v]) => k !== 'user' && v.items.some(i => !i.separator))"
                :key="key"
                :links="section.items"
                @navigate-to-page="navigateToPage"
                :user-permissions="userPermissions"
                :label="{label: section.label, defaultRoute: null, badge: section.badge || 0}"
              />
            </div>
            <div v-else class="flex flex-row justify-end ml-auto font-semibold font-heading items-center space-x-4 text-xs">
              <!-- External Links Go Here-->
              <PrimeMenu v-if="navbar_links.accounting.items.some(i => !i.separator)" :links="navbar_links.accounting.items" @navigate-to-page="navigateToPage" :user-permissions="userPermissions" :label="{label: navbar_links.accounting.label, defaultRoute: null}" />

            </div>
            <div class="flex flex-row justify-end items-center">
              <PrimeMenu :links="navbar_links.user.items" @navigate-to-page="navigateToPage" :user-permissions="userPermissions" :label="{label: `${loggedInUserFullName}`, defaultRoute: null}" />
            </div>
          </div>
        </nav>
      </section>
    </div>
  </template>
  
  <script>
  export default {
    name: "NavBar",
  }
  </script>
  
  <script setup>
  import { computed, ref, defineProps, watch, onMounted } from 'vue'
  import { useStore } from 'vuex'
  import { useRoute, useRouter } from 'vue-router';
  import BreadCrumb from '@/components/BreadCrumb.vue';
  import MenuBar from '@/components/menu-bar-components/MenuBar.vue';
  import { useClickOutside } from '@/composables/useClickOutside';
  // import { getCampaigns } from '@/services/Campaign';
  import { createPageVisitLog } from '@/services/PageVisitLog';
  import HeadlessPopoverClick from '../components/HeadlessPopoverClick.vue'
  import NavLink from './nav-bar-link-components/NavLink.vue';
  import PrimePanelMenu from './nav-bar-link-components/PrimePanelMenu.vue';
  import PrimeMenu from './nav-bar-link-components/PrimeMenu.vue';
import { getOutreachDraftStatusSummary } from '@/services/OutreachDraft';

    
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  
  const props = defineProps({
    windowWidth: {
      type: Number,
      required: true
    }
  })

  const isNavDropdownVisible = ref(false)
  const navBreakpoint = 1280
  const navBoundary = ref(null)
  
  const previousTitle = ref('')
  const currentTitle = ref('')
  
  const isLoggedIn = computed(function () {
    return store.getters['auth/isAuthenticated']
  })
  onMounted(() => {
    configureResearchLinks();
  });

  const showMenuBar = computed(() => {
    if(route.meta.viewMenuConfig) {
      if(route.meta.viewMenuConfig.hasShowAllHideAll) {
        return true
      }
      if(route.meta.viewMenuConfig.hasFilters) {
        return true
      }
      if(route.meta.viewMenuConfig.hasBulkOps) {
        return true
      }
      if(route.meta.viewMenuConfig.hastStatusSummary) {
        return true
      }
    }
    return false
  })

  const pageTitle = computed(() => {
    // console.log('route', route)
    if(route.name) {
      if(route.name === 'quick-action-companies'){
          return route.params.crmStatus == 2 ? 'High Priority Companies' : route.params.crmStatus == 7 ? 'In Dialogue Companies' : route.params.crmStatus == 10 ? 'Not Now/Keep Warm Companies' : route.params.crmStatus == 9 ? 'Responded/Coverage Companies' : null
      } else if (route.name === 'configurable-companies') {
        return selectedPageConfigName.value
      } else {
          return route.meta.pageTitle
      }
    } else {
      return route.meta.pageTitle
    }
  })

  watch(pageTitle, () => {
    // console.log('page title', pageTitle.value)
    document.title = pageTitle.value
    currentTitle.value = pageTitle.value
  })
  
  const selectedPageConfigName = computed(() => store.getters['pageConfigs/showSelectedPageConfigName'])
  
  watch(() => props.windowWidth, (currentWidth) => {
    if (currentWidth > navBreakpoint) {
      hideNavDropdown()
    }
  })
  
  useClickOutside(navBoundary, () => {
    hideNavDropdown()
  })
  
  const researchLinks = ref([
  ])
  
  const contactLinks = ref([
    { route: '/contacts', label: 'All Contacts', command: () => { navigateToPage('/contacts', 'Contacts') } },
    { separator: true },
    { route: '/referrers', label: 'Referral Sources', command: () => {
        // router.push('/referrers')
        navigateToPage('/referrers', 'Referrers')
    }}
  ])

  function navigateToPage(route, pageName){
    if(pageName === 'Companies'){
      setPageConfig(researchPageConfigs.value.find((config) => config.id == 1))
      logLinkSelection(selectedPageConfigName.value)
    } else {
      router.push(route)
      logLinkSelection(pageName)
    }
    hideNavDropdown()
  }

  const accountingLinks = ref([
            { route: '/engagements', label: 'Engagements', viewEngagementsInvoicesLink: true,
        command: () => {
            // router.push('/engagements');
            navigateToPage('/engagements', 'Engagements')
        }   },
            { route: '/invoices', label: 'Invoices', viewEngagementsInvoicesLink: true,
        command: () => {
            // router.push('/invoices');
            navigateToPage('/invoices', 'Invoices')
        }   },
            { route: '/invoice_reminders', label: 'Invoice Reminders', accountingLink: true,
        command: () => {
            // router.push('/invoice_reminders');
            navigateToPage('/invoice_reminders', 'Invoice Reminders')
        }   },
            { route: '/service-records', label: 'Service Records', devOrMgmt: true,
        command: () => {
            // router.push('/service-records');
            navigateToPage('/service-records', 'Service Records')
        }  },
            {
                label: 'Inventory', inventoryLink: true,
                items: [
                    { route: '/inventory-items', label: 'Inventory', inventoryLink: true,
                command: () => {
                    navigateToPage('/inventory-items', 'Inventory')
                }   },
                    { route: '/inventory-by-person', label: 'By Person', inventoryLink: true,
                command: () => {
                    navigateToPage('/inventory-by-person', 'Inventory By Person')
                }   },
                    { route: '/inventory-warranties', label: 'Warranties', inventoryLink: true,
                command: () => {
                    navigateToPage('/inventory-warranties', 'Warranties')
                }   },
                ],
            },
            { separator: true },
            { route: '/expenses', label: 'Expenses', expenseParticipation: true,
        command: () => {
            navigateToPage('/expenses', 'Expenses')
        }   },
            { route: '/team-expenses', label: 'Team Expenses', expenseParticipation: true,
        command: () => {
            navigateToPage('/team-expenses', 'Team Expenses')
        }   },
            { route: '/client-expenses', label: 'Client Expenses', clientExpensesLink: true,
        command: () => {
            navigateToPage('/client-expenses', 'Client Expenses')
        }   },
            { route: '/accounting-queue', label: 'Accounting Queue', accountingQueueLink: true,
        command: () => {
            navigateToPage('/accounting-queue', 'Accounting Queue')
        }   },
            { route: '/expense-reports', label: 'Expense Reports', expenseAccounting: true,
        command: () => {
            navigateToPage('/expense-reports', 'Expense Reports')
        }   },
  ])

  
  const clientLinks = ref([
    { route: '/clients', label: 'All Clients', command: () => { navigateToPage('/clients', 'Clients') } },
    { route: '/client-assignments', label: 'Client Assignments', command: () => { navigateToPage('/client-assignments', 'Client Assignments') } },
    { separator: true },
    {
        label: "Campaigns",
        items: [
            { route: '/campaigns', label: 'Manage Campaigns',
        command: () => {
            // router.push('/campaigns');
            navigateToPage('/campaigns', 'Manage Campaigns')
        } },
            { route: '/company-campaign-change-requests', label: 'Company Campaign Change Requests',
        command: () => {
            // router.push('/company-campaign-change-requests');
            navigateToPage('/company-campaign-change-requests', 'Company Campaign Change Requests')
        } },
            { route: '/campaign_change_report', label: 'Campaign Change Report',
        command: () => {
            // router.push('/campaign_change_report');
            navigateToPage('/campaign_change_report', 'Campaign Change Report')
        } },
        ]
    },
    { route: '/crm-reporting', label: 'CRM Reporting',
        command: () => {
            navigateToPage('/crm-reporting', 'CRM Reporting')
        }  },
    { route: '/client_appendices', label: 'Companies for Client Appendices',
        command: () => {
            navigateToPage('/client_appendices', 'Client Appendices')
        }  },
    { route: '/client_tasks_spreadsheet', label: 'Client Tasks Spreadsheet',
        command: () => {
            navigateToPage('/client_tasks_spreadsheet', 'Client Tasks Spreadsheet')
        }  },
    { route: '/client-transactions', label: 'Client Transactions', devOrMgmt: true,
        command: () => {
            navigateToPage('/client-transactions', 'Client Transactions')
        }  },
    { route: '/contingency-payments', label: 'Contingency Payments', devOrMgmt: true,
        command: () => {
            navigateToPage('/contingency-payments', 'Contingency Payments')
        }  },
  ])

  
  const quickActionLinks = ref([
        { route: '/quick-action-companies/2', label: 'High Priority Companies',
        command: () => {
            // router.push('/quick-action-companies/2');
            navigateToPage('/quick-action-companies/2', 'High Priority Companies')
        } },
        { route: '/quick-action-companies/7', label: 'In Dialogue Companies',
        command: () => {
            // router.push('/quick-action-companies/7');
            navigateToPage('/quick-action-companies/7', 'In Dialogue Companies')
        }  },
        { route: '/quick-action-companies/10', label: 'Not Now/Keep Warm Companies',
        command: () => {
            // router.push('/quick-action-companies/10');
            navigateToPage('/quick-action-companies/10', 'Not Now/Keep Warm Companies')
        }  },
        { route: '/quick-action-companies/9', label: 'Responded/Coverage Companies',
        command: () => {
            // router.push('/quick-action-companies/9');
            navigateToPage('/quick-action-companies/9', 'Responded/Coverage Companies')
        }  },
      {
          separator: true
      },
        { route: '/quick-action-companies-client/', label: 'Quick Actions by Client',
        command: () => {
            // router.push('/quick-action-companies-client');
            navigateToPage('/quick-action-companies-client/', 'Quick Actions by Client')
        }   },
        { route: '/crm_details_spreadsheet/', label: 'CRM Details Spreadsheet',
        command: () => {
            // router.push('/crm_details_spreadsheet/');
            navigateToPage('/crm_details_spreadsheet/', 'CRM Details Spreadsheet')
        }   },
        { route: '/user_crm_details_spreadsheet/', label: 'My CRM Details Spreadsheet',
        command: () => {
            // router.push('/crm_details_spreadsheet/');
            navigateToPage('/user_crm_details_spreadsheet/', 'My CRM Details Spreadsheet')
        }   },
    ])

    const outreachLinks = ref([
      { route: '/email-templates', label: 'Email Templates',
        command: () => {
            navigateToPage('/email-templates', 'Email Templates')
          } 
      },
      {
          route: '/outreach-drafts', label: 'Outreach Emails',
          command: () => {
              navigateToPage('/outreach-drafts', 'Outreach Emails')
          }
      },
      {
          route: '/manage-mailbox', label: 'Manage Mailbox',
          command: () => {
              navigateToPage('/manage-mailbox', 'Manage Mailbox')
          }
      },
      {
          route: '/outreach-responses', label: 'Outreach Responses', devOnly: true,
          command: () => {
              navigateToPage('/outreach-responses', 'Outreach Responses')
          }
      },
      {
          route: '/outreach-candidates', label: 'Outreach Candidates',
          command: () => {
              navigateToPage('/outreach-candidates', 'Outreach Candidates')
          }
      },
    ])
  
  const backOfficeLinks = ref([
    { route: '/back-office/requests', label: 'My Requests', backOfficeRequestLink: true,
      command: () => {
        navigateToPage('/back-office/requests', 'My Requests')
      }  },
    { route: '/back-office/triage', label: 'Request Triage', backOfficeLink: true,
      command: () => {
        navigateToPage('/back-office/triage', 'Request Triage')
      }  },
    { route: '/back-office/request-backlog', label: 'Request Backlog', backOfficeRequestLink: true,
      command: () => {
        navigateToPage('/back-office/request-backlog', 'Request Backlog')
      }  },
    { separator: true },
    { route: '/back-office/backlog', label: 'Backlog', backOfficeLink: true,
      command: () => {
        navigateToPage('/back-office/backlog', 'Backlog')
      }  },
    { route: '/back-office/roadmap', label: 'Roadmap', backOfficeLink: true,
      command: () => {
        navigateToPage('/back-office/roadmap', 'Roadmap')
      }  },
    { route: '/back-office/blockers', label: 'Blockers', backOfficeLink: true,
      command: () => {
        navigateToPage('/back-office/blockers', 'Blockers')
      }  },
    { route: '/back-office/runbook', label: 'Operational Runbook', backOfficeLink: true,
      command: () => {
        navigateToPage('/back-office/runbook', 'Operational Runbook')
      }  },
])

  const adminLinks = ref([
    {route: '/admin-dashboard', label: 'Dashboard', devOrMgmt: true, isNew: true, command: () => {
            navigateToPage('/admin-dashboard', 'Admin Dashboard')
        } },
    {route: '/job_queue', label: 'Job Queue', command: () => {
            // router.push('/job_queue/');
            navigateToPage('/job_queue', 'JobQueue')
        } },
    {route: '/admin', label: 'Admin Tools', devOrMgmt: true, command: () => {
        // router.push('/job_queue/');
        navigateToPage('/admin', 'Admin Tools')
    } },
    {route: '/admin_import_tools', label: 'Admin Import Tools', devOrMgmt: true, command: () => {
        // router.push('/job_queue/');
        navigateToPage('/admin_import_tools', 'Admin Import Tools')
    } },
    {route: '/all-users', label: 'All Users', viewPeoplePage: true, command: () => {
        // router.push('/job_queue/');
        navigateToPage('/all-users', 'All Users')
    } },
    {route: '/assimilation-logs', label: 'Assimilation Logs', devOrMgmt: true, command: () => {
        // router.push('/job_queue/');
        navigateToPage('/assimilation-logs', 'Assimilation Logs')
    } },
    { separator: true },
    {route: '/admin/all-expenses', label: 'All Expenses (Admin)', expenseAdmin: true, command: () => {
        navigateToPage('/admin/all-expenses', 'All Expenses (Admin)')
    } },
    {route: '/admin/user-groups', label: 'User Groups', devOrMgmt: true, command: () => {
        navigateToPage('/admin/user-groups', 'User Groups')
    } },
    {route: '/admin/mcp-tokens', label: 'MCP Tokens', devOrSuperuser: true, command: () => {
        navigateToPage('/admin/mcp-tokens', 'MCP Tokens')
    } },
    {route: '/admin/reporting-relationships', label: 'Reporting Relationships', devOrMgmt: true, command: () => {
        navigateToPage('/admin/reporting-relationships', 'Reporting Relationships')
    } },
    {route: '/admin/timecard-managers', label: 'Timecard Managers', devOrMgmt: true, command: () => {
        navigateToPage('/admin/timecard-managers', 'Timecard Managers')
    } },
    {route: '/admin/engagement-picker', label: 'Engagement Picker', devOrMgmt: true, command: () => {
        navigateToPage('/admin/engagement-picker', 'Engagement Picker')
    } },
    {route: '/admin/submission-delegates', label: 'Submission Delegates', expenseAdmin: true, command: () => {
        navigateToPage('/admin/submission-delegates', 'Submission Delegates')
    } },
    {route: '/admin/expense-categories', label: 'Expense Categories', devOrMgmt: true, command: () => {
        navigateToPage('/admin/expense-categories', 'Expense Categories')
    } },
  ])
  
const userLinks = ref([
    {route: '/profile', label: 'Profile', command: () => {
        // router.push('/job_queue/');
        navigateToPage('/profile', 'Profile')
      }
    },
    {route: '/training-documents', label: 'Training Docs', command: () => {
        // router.push('/job_queue/');
        navigateToPage('/training-documents', 'Training Documents')
      }
    },
    {route: '/connect-to-claude', label: 'Connect to Claude', command: () => {
        // router.push('/job_queue/');
        navigateToPage('/connect-to-claude', 'Connect to Claude')
      }
    },
    {route: '/auth', label: 'Log Out', command: () => {
        // router.push('/job_queue/');
        logout()
      }
    },
])

function canViewLink(link){
    const p = userPermissions.value
    if (!p) return false
    if (link.separator) return true
    if(link.devOnly) return p.isDev
    if(link.devOrMgmt) return p.isDev || p.isManagement
    if(link.devOrSuperuser) return p.isDev || p.isSuperuser
    if(link.viewPeoplePage) return p.isDev || p.isManagement || p.isSuperUser || p.isViewUserWorkflow
    if(link.accountingLink) return p.isDev || p.isManagement || p.isSuperuser || p.isAccountingGroup
    if(link.viewEngagementsInvoicesLink) return p.isDev || p.isManagement || p.isSuperuser || p.isAccountingGroup || p.isViewEngagementsInvoices
    if(link.inventoryLink) return p.isDev || p.isManagement || p.isInventory
    if(link.expenseAccounting){
      if(p.isDev || p.isSuperuser || p.isExpenseAdmin) return true
      return p.isExpenseAccounting && p.isExpenseParticipation
    }
    if(link.accountingQueueLink) return !!p.canAccessAccountingQueue
    if(link.clientExpensesLink) return !!(p.canAccessClientExpenses || p.isDev || p.isSuperuser || p.isExpenseAdmin || p.isExpenseClientView)
    if(link.expenseAdmin) return p.isDev || p.isSuperuser || p.isExpenseAdmin
    if(link.expenseParticipation) return p.isDev || p.isSuperuser || p.isExpenseAdmin || p.isExpenseParticipation
    if(link.backOfficeLink) return p.isDev || p.isSuperuser || p.isBackOffice
    if(link.backOfficeRequestLink) return p.isDev || p.isSuperuser || p.isBackOffice || p.isBackOfficeRequest
    return true
}

function filterLinks(links){
    const visible = links
        .filter(link => canViewLink(link))
        .map(link => link.items ? { ...link, items: filterLinks(link.items) } : link)
    return dropStrandedSeparators(visible)
}

// A divider only means something between two visible entries. Once
// permissions have removed the links on one side of it, a separator left in
// place renders as a rule hanging off the top or bottom of the menu -- most
// visibly for someone who can see a single item in a section.
function dropStrandedSeparators(links){
    return links.filter((link, i) => {
        if(!link.separator) return true
        const before = links.slice(0, i).some(l => !l.separator)
        const after = links.slice(i + 1).some(l => !l.separator)
        if(!before || !after) return false
        return !links[i - 1]?.separator
    })
}



const navbar_links = computed(() => ({
    companies:    { label: "Companies",    items: filterLinks(companyComputedLinks.value) },
    // quick_actions:{ label: "Quick Actions",items: filterLinks(quickActionLinks.value) },
    outreach:{ label: "Outreach", badge: outreachAlertCount.value, items: filterLinks(outreachLinks.value) },
    clients:      { label: "Clients",      items: filterLinks(clientLinks.value) },
    research:     { label: "Research",     items: filterLinks(researchLinks.value) },
    contacts:     { label: "Contacts",     items: filterLinks(contactLinks.value) },
    accounting:   { label: "Accounting",   items: filterLinks(accountingLinks.value) },
    back_office:  { label: "Back Office",  items: filterLinks(backOfficeLinks.value) },
    admin:        { label: "Admin",        items: filterLinks(adminLinks.value) },
    user:         { label: loggedInUserFullName.value, items: filterLinks(userLinks.value) },
}))



  function configureResearchLinks() {
    researchLinks.value.push({ route: '/research_dashboard', label: 'Research Dashboard', command: () => { navigateToPage('/research_dashboard', 'Research Dashboard') } });
    researchLinks.value.push({ separator: true });
    if ( userPermissions.value.isSuperuser || userPermissions.value.isDev || userPermissions.value.isManagement || userPermissions.value.isHourly || userPermissions.value.isResearch || userPermissions.value.isResearchLead || userPermissions.value.isAccountManager || userPermissions.value.isAccountStaff) {
    //   researchLinks.value.push({route: '/research_dashboard', label: 'Research Dashboard', command: () => {
    //     router.push('/research_dashboard')
    //   }});

    researchLinks.value.push({route: '/data-requests', label: 'Data Requests', command: () => {
        // router.push('/data-requests')
        navigateToPage('/data-requests', 'Data Requests')
      }});
      researchLinks.value.push({route: '/research_assignments', label: 'Research Assignments', command: () => {
        // router.push('/research_assignments')
        navigateToPage('/research_assignments', 'Research Assignments')
      }});
      researchLinks.value.push({ route: '/company-geo-location', label: 'Company Geo Location', command: () => {
        navigateToPage('/company-geo-location', 'Company Geo Location')
      } });
      researchLinks.value.push({ route: '/dla_qpl_companies', label: 'DLA QPL Companies', command: () => {
        // router.push('/dla_qpl_companies')
        navigateToPage('/dla_qpl_companies', 'DLA QPL Companies')
      } });
    }
    if ( userPermissions.value.isSuperuser || userPermissions.value.isDev || userPermissions.value.isManagement || userPermissions.value.isAccountManager || userPermissions.value.isAdvancedResearch || userPermissions.value.isAccountStaff) {
      researchLinks.value.push({route: '/pma_list', label: 'All Parts Manufacturer Approvals', command: () => {
        // router.push('/pma_list')
        navigateToPage('/pma_list', 'All Parts Manufacturer Approvals')
      }});
      researchLinks.value.push({route: '/pma_resolution_review', label: 'PMA Resolution Review', command: () => {
        navigateToPage('/pma_resolution_review', 'PMA Resolution Review')
      }});
      // researchLinks.value.push({route: '/data-requests', label: 'Data Requests', command: () => {
      //   // router.push('/data-requests')
      //   navigateToPage('/data-requests', 'Data Requests')
      // }});
    }
    if ( userPermissions.value.isSuperuser || userPermissions.value.isDev || userPermissions.value.isManagement || userPermissions.value.isAccountManager || userPermissions.value.isAccountStaff) {
      researchLinks.value.push({route: '/prime_contracts', label: 'Prime Contracts', command: () => {
        // router.push('/prime_contracts')
        navigateToPage('/prime_contracts', 'Prime Contracts')
      }});
    }
  }
  
  
  function toggleNavDropdown() {
    isNavDropdownVisible.value = !isNavDropdownVisible.value
  }
  
  function hideNavDropdown() {
    isNavDropdownVisible.value = false
  }
  


const userPermissions = computed(() => store.getters['auth/showUserPermissions'] || {})

const hasInternalUserPermissions = computed(() => {
        let permissions = store.getters["auth/showUserPermissions"]
        return (
            permissions &&
            (
                permissions.isInternal ||
                permissions.isDev ||
                permissions.isManagement ||
                permissions.isSuperuser ||
                permissions.isAccountManager ||
                permissions.isAccountStaff ||
                permissions.isResearchLead ||
                permissions.isAdvancedResearch ||
                permissions.isResearch ||
                permissions.isHourly ||
                permissions.isExpenseAccounting ||
                permissions.isExpenseAdmin
            )
        )
    })

  // Count of outreach drafts that need attention -- failed sends + scheduled sends
  // overdue by 30+ min -- shown as a red badge on the Outreach menu. Sourced from
  // the same status-summary endpoint the Outreach Drafts page uses (scoped to what
  // the user can see, so dev users get the fleet-wide count). Refreshed in the
  // background after each navigation so it stays current without its own timer.
  const outreachAlertCount = ref(0)

  async function refreshOutreachAlertCount() {
    if (!hasInternalUserPermissions.value) { outreachAlertCount.value = 0; return }
    try {
      const summary = await getOutreachDraftStatusSummary(store.state.auth.token, {})
      const failed = (summary && summary.counts && summary.counts.failed) || 0
      const overdue = (summary && summary.overdue_scheduled) || 0
      outreachAlertCount.value = failed + overdue
    } catch (e) {
      // non-critical; keep the last known count rather than flicker to zero
    }
  }

  // Re-check after every page navigation (background; failures are swallowed above).
  watch(() => route.path, () => { refreshOutreachAlertCount() })
  onMounted(() => { refreshOutreachAlertCount() })


  const loggedInUserFullName = computed(function () {
    const fullName = store.getters['auth/showUserFullName']
    const username = store.getters['auth/showUsername']
    if(fullName && fullName.trim() != '') {
      return store.getters['auth/showUserFullName']
    } else if(username && username.trim() != '') {
      return username
    } else {
      return "--"
    }
  })
  
  
  const loggedInUser = computed(function () {
    return store.getters['auth/showUser']
  })
  
  
  function logLinkSelection(toPage) {
    previousTitle.value = currentTitle.value
    currentTitle.value = toPage
    const token = store.state.auth.token
    if(token) {
      let data = JSON.stringify({
        from_page_name: previousTitle.value, 
        to_page_name: toPage
      })
      createPageVisitLog(token, data)
    }
  }
  
  function setPageConfig(config){
    if(route.name != 'companies'){
      router.push({path:'/companies'})
    }
    if(route.name != 'ba-crm-v2') {
      store.dispatch('pageConfigs/setSelectedPageConfig', config)
    }
    hideNavDropdown()
  }
  
  const researchPageConfigs = computed(() => {
    const pageConfigs = store.state.pageConfigs.pageConfigs
    // console.log('page configs', pageConfigs)
    return pageConfigs.filter(config => config.menu_location == 0)
  })

  const publicConfigs = computed(() => {
    return researchPageConfigs.value.filter(config => config.is_public && config.id != 1)
  })

  const privateConfigs = computed(() => {
    return researchPageConfigs.value.filter(config => !config.is_public)
  })

const companyComputedLinks = computed(() => {
  const user = store.state.auth.user
  let publicPageConfigs = publicConfigs.value.map((config) => {
    let configLink = {route: '/companies', label: config.configuration_name, command: () => {
      setPageConfig(config)
      logLinkSelection(config.configuration_name)
    }}
    if(config.user && config.user.id == user.id){
      configLink.items = [
        {label: "Edit", route: '/manage_page_config/' + config.id, command: () => {
          // router.push(`/manage_page_config/${config.id}`)
          navigateToPage(`/manage_page_config/${config.id}`, 'Edit Page Config')
        }}
      ]
    }
    return configLink
  })
  const baCrmLinks = publicPageConfigs.filter(link => link.label == 'BA CRM')
  publicPageConfigs = publicPageConfigs.filter(link => link.label != 'BA CRM')
  publicPageConfigs.push({label: "Private Equity Companies", isNew: true, isBeta: false, route: "/private-equity-companies", command: () => {navigateToPage('/private-equity-companies', 'Private Equity Companies')}})
  const privatePageConfigs = privateConfigs.value.map((config) => {
    let configLink =  {route: '/companies', label: config.configuration_name, command: () => {
      setPageConfig(config)
      logLinkSelection(config.configuration_name)
    }}
    if(config.user && config.user.id == user.id){
      configLink.items = [
        {label: "Edit", route: '/manage_page_config/' + config.id, command: () => {
          // router.push(`/manage_page_config/${config.id}`)
          navigateToPage(`/manage_page_config/${config.id}`, 'Edit Page Config')
        }}
      ]
    }
    return configLink
  })
  return [
    {label: "BA CRM V2", route: "/company-research", command: () => {navigateToPage('/company-research', 'BA CRM V2')}},
    ...baCrmLinks,
    { label: 'All Companies', route: '/companies', command: () => { navigateToPage('/companies', 'Companies') } },
    { separator: true },
    {
      label: "Public Pages",
      items: publicPageConfigs
    },
    {
      label: "Private Pages",
      items: privatePageConfigs
    },
    {
      label: "Create New Page",
      route: "/create_page_config",
      command: () => {
        // router.push('/create_page_config')
        navigateToPage('/create_page_config', 'Create New Page')
      }
    },
    {
        label: "Quick Actions",
        items: filterLinks(quickActionLinks.value)
    },
  ]
})
  
  function logout() {
    store.dispatch('auth/logout')
    router.push({ name: 'login' });
  }
  
  
  // const crmPageConfigs = computed(() => {
  //   const pageConfigs = store.state.pageConfigs.pageConfigs
  //   return pageConfigs.filter(config => config.menu_location == 1)
  // })
  
// const companyLinkGroups = ref([])
// const companyAdditionalLinks = ref([])




function addTimecardsLink(){
  if(userPermissions.value.isAccountManager || userPermissions.value.isHourly || userPermissions.value.isExternalTimecard || userPermissions.value.isDev || userPermissions.value.isSuperuser){
    // Insert directly after Service Records so Timecards sits with the
    // other internal accounting links, not at the end of the expense group.
    const timecardsLink = {
      route: '/timecards',
      label: 'Timecards',
      command: () => {
        navigateToPage('/timecards', 'Timecards')
      },
    }
    const serviceRecordsIndex = accountingLinks.value.findIndex(
      (link) => link && link.route === '/service-records'
    )
    const insertAt = serviceRecordsIndex === -1
      ? accountingLinks.value.length
      : serviceRecordsIndex + 1
    accountingLinks.value.splice(insertAt, 0, timecardsLink)
  }
}

addTimecardsLink()

  // async function setCampaignActivityLinks() {
  //   const token = store.state.auth.token
  //   const result = await getCampaigns(token)
  //   links.value = [{ href: '/campaign_activities_summary', label: 'Campaign Activities Summary' }, ...result.map(campaign => {
  //     return { href: `/campaign_activities/${campaign.id}`, label: `${campaign.campaign_name} Activities` }
  //   })]
  //   newCrmLinks.value = [...result.map(campaign => {
  //     return { href: `/new_crm/${campaign.id}`, label: `${campaign.campaign_name} Activities` }
  //   })]
  // }
  
//   function fetchPageConfigs() {
//   // console.log('fetch page configs ran')
//   // store.dispatch('pageConfigs/setPageConfigs')
// }


  async function fetchAndLoadPageConfigs() {
    // Page configs are only meaningful for users with access to company
    // pages. Expense-only users (and other narrow roles) don't have
    // permission to query this endpoint, and triggering it would 403 →
    // global interceptor → /forbidden-page redirect.
    const p = store.getters['auth/showUserPermissions'] || {}
    const canViewCompanyPages = (
      p.isDev || p.isSuperuser || p.isManagement ||
      p.isAccountManager || p.isAccountStaff ||
      p.isResearch || p.isResearchLead || p.isAdvancedResearch ||
      p.isHourly
    )
    if(canViewCompanyPages){
      await store.dispatch('pageConfigs/setPageConfigs')
      store.dispatch('pageConfigs/tryToSetPageConfig')
    }
  }
  fetchAndLoadPageConfigs()
  </script>

<style>
.p-tieredmenu-item-label{
  font-family: poppins;
  white-space: preserve nowrap;
}

</style>
  